import {
  BUYER,
  CL_APPROVED,
  PRODUCTS,
  REJECTED_BY_COUNTRY_MATRIX,
  SELLER,
} from "codes";
import type { Account, CreditLimit, Partner, PartnerData } from "types";

const normalizeAccountProduct = (account_product?: string | null) =>
  (account_product ?? "").toLowerCase().trim().replace(/\s+/g, "_");

export const isReverseFactoringAccountProduct = (
  account_product?: string | null,
) => normalizeAccountProduct(account_product) === PRODUCTS.reverse_factoring;

const isBuyerProfile = (profile?: string | null) =>
  (profile ?? "").toLowerCase().trim() === BUYER.toLowerCase();

const isSellerProfile = (profile?: string | null) =>
  (profile ?? "").toLowerCase().trim() === SELLER.toLowerCase();

export const isReverseFactoringBuyerAccount = (
  account?: Pick<Account, "account_product" | "profile"> | null,
): boolean => {
  if (!account) {
    return false;
  }

  return (
    isReverseFactoringAccountProduct(account.account_product) &&
    isBuyerProfile(account.profile)
  );
};

export const reverseFactoringPartnerAddBlockedMessage =
  "Add supplier partners first, then request and allocate buyer credit limit.";

export const reverseFactoringSupplierClBuyerViewMessage =
  "Supplier in Reverse Factoring cannot request credit limits. Please switch to the Buyer view to request a credit limit.";

export const reverseFactoringSupplierNoAllocationMessage =
  "No credit limit has been allocated to this supplier yet. Once the buyer allocates a limit from the Buyer credit limit tab, you can create a term sheet for this supplier.";

export const canAddPartnerForReverseFactoringBuyer = (
  account?: Pick<Account, "account_product" | "profile"> | null,
  _buyerCreditLimit?: CreditLimit | null,
) => {
  if (!isReverseFactoringBuyerAccount(account)) {
    return true;
  }

  return true;
};

/** Whether onboarding partner details are complete enough to show read-only view. */
export const isPartnerDetailsComplete = (partner: Partner): boolean =>
  Boolean(
    partner.persistedFromApi ||
      (partner.address_line1 !== undefined &&
        partner.industry !== undefined &&
        partner.goods_category !== undefined &&
        partner.annual_revenue !== undefined &&
        partner.number_of_past_paid_transaction !== undefined &&
        partner.average_invoice_amount !== undefined &&
        partner.average_payment_terms !== undefined &&
        partner.any_delays_of_payment_with_buyer !== undefined &&
        partner.any_credit_note !== undefined),
  );

/** Map persisted API partners into onboarding form list entries. */
export const mapApiPartnersToFormList = (
  apiPartners: PartnerData[],
): Partner[] =>
  apiPartners.map((partner) => ({
    id: partner.id,
    registration_number: partner.reg_no,
    name: partner.name,
    country: partner.country,
    iso3: partner.country_eligiblilty?.country_iso3 ?? "",
    is_identified: true,
    showDelete: true,
    persistedFromApi: true,
    industry: partner.industry,
    goods_category: partner.goods_category,
    annual_revenue: partner.annual_revenue,
    average_invoice_amount: partner.average_invoice_amount,
    average_payment_terms: partner.average_payment_terms,
    number_of_past_paid_transaction: partner.number_of_past_paid_transaction,
  }));

export const mergePartnerLists = (
  existing: Partner[],
  incoming: Partner[],
): Partner[] => {
  const existingRegNos = new Set(
    existing.map((partner) => partner.registration_number),
  );
  return [
    ...existing,
    ...incoming.filter(
      (partner) => !existingRegNos.has(partner.registration_number),
    ),
  ];
};

/** RF supplier blocked from buyer credit-limit allocation (live matrix or existing CL rejection). */
export const isRfPartnerCountryMatrixBlocked = (
  partner?: Pick<PartnerData, "country_matrix_blocked" | "credit_limit"> | null,
): boolean => {
  if (!partner) {
    return false;
  }

  if (partner.country_matrix_blocked) {
    return true;
  }

  return partner.credit_limit?.internal_status === REJECTED_BY_COUNTRY_MATRIX;
};

export const isReverseFactoringBuyerCreditLimitApproved = (
  creditLimit?: Pick<CreditLimit, "status" | "approved_amount"> | null,
): boolean =>
  creditLimit?.status === CL_APPROVED &&
  creditLimit.approved_amount !== null &&
  creditLimit.approved_amount !== undefined;

export const hasApprovedReverseFactoringSupplierAllocation = (
  partner?: Pick<PartnerData, "credit_limit"> | null,
): boolean =>
  partner?.credit_limit?.approved_amount !== null &&
  partner?.credit_limit?.approved_amount !== undefined &&
  Number(partner.credit_limit.approved_amount) > 0;

/**
 * RF supplier partner CL status:
 * - Internal flow: buyer CL approved → all linked supplier partners show approved.
 * - Client flow: buyer CL approved + positive supplier allocation → approved.
 */
export const isReverseFactoringSupplierPartnerClApproved = ({
  lead,
  partner,
  requireSupplierAllocation = false,
}: {
  lead?: Pick<Account, "account_product" | "profile" | "credit_limit"> | null;
  partner?: Pick<
    PartnerData,
    "profile" | "credit_limit" | "country_matrix_blocked"
  > | null;
  requireSupplierAllocation?: boolean;
}): boolean => {
  if (!lead || !partner || isRfPartnerCountryMatrixBlocked(partner)) {
    return false;
  }

  const isRfBuyerLead = isReverseFactoringBuyerAccount(lead);
  const isSupplierPartner =
    isSellerProfile(partner.profile) ||
    (isRfBuyerLead && !isBuyerProfile(partner.profile));
  const isBuyerCreditLimitApproved = isReverseFactoringBuyerCreditLimitApproved(
    lead.credit_limit,
  );

  if (!isRfBuyerLead || !isSupplierPartner || !isBuyerCreditLimitApproved) {
    return false;
  }

  if (requireSupplierAllocation) {
    return hasApprovedReverseFactoringSupplierAllocation(partner);
  }

  return true;
};

/**
 * Client-side RF supplier rows: approved once buyer CL is approved and a positive
 * supplier allocation exists, even if the returned status field lags behind.
 */
export const isReverseFactoringSupplierAllocationApproved = (args: {
  lead?: Pick<Account, "account_product" | "profile" | "credit_limit"> | null;
  partner?: Pick<
    PartnerData,
    "profile" | "credit_limit" | "country_matrix_blocked"
  > | null;
}): boolean =>
  isReverseFactoringSupplierPartnerClApproved({
    ...args,
    requireSupplierAllocation: true,
  });

/** RF buyer finished step 4 when approved limit is fully allocated to eligible suppliers. */
export const isReverseFactoringBuyerAllocationComplete = ({
  buyerCreditLimit,
  partners,
}: {
  buyerCreditLimit?: Pick<CreditLimit, "status" | "approved_amount"> | null;
  partners?: Array<
    Pick<PartnerData, "credit_limit" | "country_matrix_blocked">
  > | null;
}): boolean => {
  if (!isReverseFactoringBuyerCreditLimitApproved(buyerCreditLimit)) {
    return false;
  }

  const eligiblePartners = (partners ?? []).filter(
    (partner) => !isRfPartnerCountryMatrixBlocked(partner),
  );
  if (!eligiblePartners.length) {
    return false;
  }

  const allocationTarget = Number(buyerCreditLimit?.approved_amount ?? 0);
  if (allocationTarget <= 0) {
    return false;
  }

  const totalAllocated = eligiblePartners.reduce((sum, partner) => {
    if (!hasApprovedReverseFactoringSupplierAllocation(partner)) {
      return sum;
    }
    return sum + Number(partner.credit_limit?.approved_amount ?? 0);
  }, 0);

  return totalAllocated === allocationTarget;
};
